import { useState } from 'react';
import type { ErrorField } from '../types/ErrorField';
import type { AddJudicialDepositValue } from './useLegalCaseDeposits';

// Campos com binding próprio na tela; qualquer outro 422 do backend cai no bloco residual.
const INLINE_FIELDS = new Set([
    'blockedAmount',
    'blockedAt',
    'courtRecordsPage',
    'description',
]);

export const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !INLINE_FIELDS.has(e.fieldName));

// O valor bloqueado usa CurrencyInput (value numérico); 0 = "não informado" (o form começa
// zerado), então não é enviado — o backend recusa (422) e a mensagem aparece no campo.
const moneyOrUndefined = (value: number): number | undefined =>
    value && value > 0 ? value : undefined;

interface UseLegalCaseDepositFormArgs {
    onSubmit: (value: AddJudicialDepositValue) => Promise<boolean>;
}

// Estado e handlers do formulário de criação de depósito (o bloqueio), fora do componente
// (diretriz do repo: sem função dentro de função). O levantamento não faz parte deste form —
// é registrado depois, editando o depósito (ver LegalCaseDeposits.tsx). Centraliza o reset ao
// enviar com sucesso.
export default function useLegalCaseDepositForm({ onSubmit }: UseLegalCaseDepositFormArgs) {
    const [blockedAmount, setBlockedAmount] = useState(0);
    const [blockedAt, setBlockedAt] = useState('');
    const [courtRecordsPage, setCourtRecordsPage] = useState('');
    const [description, setDescription] = useState('');

    const reset = () => {
        setBlockedAmount(0);
        setBlockedAt('');
        setCourtRecordsPage('');
        setDescription('');
    };

    // O valor bloqueado é obrigatório, mas o botão fica sempre habilitado — o feedback vem do
    // backend (422), como nas demais seções.
    const canSubmit = true;

    const submit = async () => {
        const ok = await onSubmit({
            blockedAmount: moneyOrUndefined(blockedAmount),
            blockedAt: blockedAt.trim() || undefined,
            courtRecordsPage: courtRecordsPage.trim() || undefined,
            description: description.trim() || undefined,
        });
        if (ok) reset();
    };

    return {
        blockedAmount, setBlockedAmount,
        blockedAt, setBlockedAt,
        courtRecordsPage, setCourtRecordsPage,
        description, setDescription,
        canSubmit, submit, reset,
    };
}
